import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Search } from 'lucide-react';
import type { PublicMarketplaceCategory } from 'modules/test/types';
import { cn } from 'lib/utils';
import { categorySurface, categoryTheme } from './categoryThemes';

/** Subjects named on a card before the rest collapse into "+N". */
const SUBJECTS_ON_CARD = 3;

export interface HubCategory {
  id: string;
  label: string;
  testCount?: number;
  subjectLabels: string[];
}

/** The API's category list in the learner's language, minus the "all" shortcut. */
export function hubCategories(
  dynamic: PublicMarketplaceCategory[],
  labelOf: (category: PublicMarketplaceCategory) => string,
  subjectLabelOf: (subject: NonNullable<PublicMarketplaceCategory['subjects']>[number]) => string,
): HubCategory[] {
  return dynamic
    .filter(({ id }) => id !== 'all')
    .map((category) => ({
      id: category.id,
      label: labelOf(category),
      testCount: category.testCount,
      subjectLabels: (category.subjects ?? []).map(subjectLabelOf),
    }));
}

interface CategoryCardProps {
  category: HubCategory;
  onOpen: () => void;
}

function CategoryCard({ category, onOpen }: CategoryCardProps) {
  const { t } = useTranslation();
  const { accent, icon: Icon } = categoryTheme(category.id);
  const shown = category.subjectLabels.slice(0, SUBJECTS_ON_CARD);
  const hidden = category.subjectLabels.length - shown.length;

  return (
    <button
      type="button"
      onClick={onOpen}
      style={categorySurface(accent)}
      className={cn(
        'relative flex min-h-[124px] flex-col overflow-hidden rounded-2xl border p-3.5 text-left transition-[transform,box-shadow] duration-200 md:min-h-[176px] md:rounded-3xl md:p-5',
        'hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-safe:hover:-translate-y-0.5',
      )}
    >
      <Icon
        aria-hidden="true"
        strokeWidth={1.2}
        className="pointer-events-none absolute -right-5 -top-5 h-28 w-28 opacity-[0.14] md:-right-6 md:-top-6 md:h-40 md:w-40"
        style={{ color: accent }}
      />
      <span className="relative font-display text-[16px] font-extrabold leading-[1.15] tracking-[-0.03em] md:text-[22px]">
        {category.label}
      </span>
      {category.testCount !== undefined && (
        <span className="relative mt-1 text-[12px] font-medium opacity-80 md:text-[13px]">
          <span className="font-semibold tabular-nums">{category.testCount}</span> {t('pager_noun_tests')}
        </span>
      )}
      {shown.length > 0 && (
        <span className="relative mt-3 hidden flex-wrap gap-1.5 md:flex">
          {shown.map((label) => (
            <span
              key={label}
              className="rounded-full bg-card/80 px-2.5 py-0.5 text-[11.5px] font-semibold text-foreground/80 ring-1 ring-inset ring-border"
            >
              {label}
            </span>
          ))}
          {hidden > 0 && (
            <span className="rounded-full bg-card/80 px-2.5 py-0.5 text-[11.5px] font-semibold tabular-nums text-foreground/80 ring-1 ring-inset ring-border">
              +{hidden}
            </span>
          )}
        </span>
      )}
      <ArrowUpRight aria-hidden="true" size={18} className="relative mt-auto self-end pt-0 opacity-70" />
    </button>
  );
}

interface CategoryHubProps {
  categories: HubCategory[];
  onOpen: (categoryId: string) => void;
  onSearch: (query: string) => void;
}

/**
 * The tests front page: one coloured card per category, then a category page of its own. Search stays here
 * so a learner who knows the test's name does not have to pick a category first.
 */
export function CategoryHub({ categories, onOpen, onSearch }: CategoryHubProps) {
  const { t } = useTranslation();
  const [query, setQuery] = React.useState('');

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = query.trim();
    if (trimmed) onSearch(trimmed);
  };

  return (
    <div className="flex flex-col gap-4 md:gap-5">
      <form role="search" onSubmit={submit} className="relative w-full sm:max-w-sm">
        <Search aria-hidden="true" size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('portal_tests_search_placeholder')}
          aria-label={t('portal_tests_search_placeholder')}
          className="h-11 w-full rounded-md border border-border bg-card pl-9 pr-3 text-base text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:h-10 md:text-sm"
        />
      </form>
      <div role="group" aria-label={t('portal_tests_categories')} className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 md:gap-4">
        {categories.map((category) => (
          <CategoryCard key={category.id} category={category} onOpen={() => onOpen(category.id)} />
        ))}
      </div>
    </div>
  );
}

export default CategoryHub;
